import React from 'react';
import { Helmet } from 'react-helmet-async';
import { 
    Business, 
    TrendingUp,
    People, 
    Security, 
    Star,
    CheckCircle
} from '@mui/icons-material';

const About = () => {
    const milestones = [
        {
            year: '2020',
            title: 'Fondation',
            description: 'Netikash est créé avec la vision de démocratiser les paiements numériques en RDC'
        },
        {
            year: '2021',
            title: 'Lancement API',
            description: 'Mise en ligne de la première version de l\'API de paiement'
        },
        {
            year: '2022',
            title: 'Croissance',
            description: 'Plus de 1000 entreprises utilisent notre plateforme'
        },
        {
            year: '2023',
            title: 'Certification PCI DSS',
            description: 'Obtention de la certification PCI DSS Level 1'
        },
        {
            year: '2024',
            title: 'Expansion',
            description: 'Ouverture du portail développeur et amélioration continue'
        }
    ];

    const values = [
        {
            title: 'Innovation',
            description: 'Nous repoussons constamment les limites de la technologie financière',
            icon: TrendingUp
        },
        {
            title: 'Sécurité',
            description: 'La protection des données de nos clients est notre priorité absolue',
            icon: Security
        },
        {
            title: 'Fiabilité',
            description: 'Nous garantissons une disponibilité et des performances optimales',
            icon: CheckCircle
        },
        {
            title: 'Simplicité',
            description: 'Nous rendons les paiements numériques accessibles à tous',
            icon: Star
        }
    ];

    const stats = [
        { label: 'Entreprises actives', value: '1000+', icon: Business },
        { label: 'Transactions/mois', value: '1M+', icon: TrendingUp },
        { label: 'Développeurs', value: '500+', icon: People },
        { label: 'Uptime', value: '99.9%', icon: CheckCircle }
    ];

    return (
        <>
            <Helmet>
                <title>À Propos - Netikash Developer Portal</title>
                <meta name="description" content="Découvrez Netikash : notre histoire, mission, valeurs et engagement envers les développeurs et les entreprises." />
                <meta property="og:title" content="À Propos de Netikash" />
                <link rel="canonical" href="https://dev.netikash.com/about" />
            </Helmet>
            <div className="min-h-screen bg-gray-50 dark:bg-slate-950 dark:text-slate-100 pt-16">
                {/* Hero Section */}
                <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center">
                            <div className="inline-flex items-center px-6 py-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-6">
                                <Business className="mr-2" />
                                <span className="font-semibold text-lg">À PROPOS DE NETIKASH</span>
                            </div>
                            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
                                Qui sommes-nous ?
                            </h1>
                            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                                Netikash est une plateforme de paiement numérique innovante qui facilite les transactions en ligne pour les entreprises et les développeurs en République Démocratique du Congo et au-delà.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    {/* Mission */}
                    <section className="mb-16">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 md:p-12">
                            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Notre Mission</h2>
                            <p className="text-xl text-gray-700 text-center max-w-3xl mx-auto leading-relaxed">
                                Démocratiser les paiements numériques en rendant les solutions de paiement en ligne accessibles, sécurisées et faciles à intégrer pour toutes les entreprises, des startups aux grandes corporations.
                            </p>
                        </div>
                    </section>

                    {/* Stats */}
                    <section className="mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">En Chiffres</h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {stats.map((stat, index) => {
                                const Icon = stat.icon;
                                return (
                                    <div key={index} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 text-center">
                                        <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                            <Icon className="text-amber-600 text-3xl" />
                                        </div>
                                        <div className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                                        <div className="text-gray-600">{stat.label}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    {/* Timeline */}
                    <section className="mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Notre Histoire</h2>
                        <div className="relative">
                            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-amber-400 to-orange-400"></div>
                            <div className="space-y-12">
                                {milestones.map((milestone, index) => (
                                    <div
                                        key={index}
                                        className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                                    >
                                        <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                                            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                                                <div className="text-2xl font-bold text-amber-600 mb-2">
                                                    {milestone.year}
                                                </div>
                                                <h3 className="text-xl font-bold text-gray-900 mb-2">
                                                    {milestone.title}
                                                </h3>
                                                <p className="text-gray-700">
                                                    {milestone.description}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="relative z-10 w-8 h-8 bg-amber-500 rounded-full border-4 border-white shadow-lg"></div>
                                        <div className="w-1/2"></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* Values */}
                    <section className="mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Nos Valeurs</h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            {values.map((value, index) => {
                                const Icon = value.icon;
                                return (
                                    <div key={index} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                                        <div className="flex items-center space-x-4 mb-4">
                                            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                                                <Icon className="text-amber-600" />
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-900">{value.title}</h3>
                                        </div>
                                        <p className="text-gray-700">{value.description}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    {/* Why Choose Us */}
                    <section className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl shadow-sm border border-amber-200 p-8 md:p-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Pourquoi Choisir Netikash ?</h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="bg-white rounded-xl p-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-3">Pour les Développeurs</h3>
                                <ul className="space-y-2 text-gray-700">
                                    <li className="flex items-start space-x-2">
                                        <CheckCircle className="text-green-500 mt-0.5" fontSize="small" />
                                        <span>API RESTful moderne et bien documentée</span>
                                    </li>
                                    <li className="flex items-start space-x-2">
                                        <CheckCircle className="text-green-500 mt-0.5" fontSize="small" />
                                        <span>Exemples de code dans plusieurs langages</span>
                                    </li>
                                    <li className="flex items-start space-x-2">
                                        <CheckCircle className="text-green-500 mt-0.5" fontSize="small" />
                                        <span>Support technique réactif</span>
                                    </li>
                                    <li className="flex items-start space-x-2">
                                        <CheckCircle className="text-green-500 mt-0.5" fontSize="small" />
                                        <span>Sandbox pour tests sans risque</span>
                                    </li>
                                </ul>
                            </div>
                            <div className="bg-white rounded-xl p-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-3">Pour les Entreprises</h3>
                                <ul className="space-y-2 text-gray-700">
                                    <li className="flex items-start space-x-2">
                                        <CheckCircle className="text-green-500 mt-0.5" fontSize="small" />
                                        <span>Certification PCI DSS Level 1</span>
                                    </li>
                                    <li className="flex items-start space-x-2">
                                        <CheckCircle className="text-green-500 mt-0.5" fontSize="small" />
                                        <span>Disponibilité 99.9% garantie</span>
                                    </li>
                                    <li className="flex items-start space-x-2">
                                        <CheckCircle className="text-green-500 mt-0.5" fontSize="small" />
                                        <span>Tarifs transparents et compétitifs</span>
                                    </li>
                                    <li className="flex items-start space-x-2">
                                        <CheckCircle className="text-green-500 mt-0.5" fontSize="small" />
                                        <span>Support dédié pour entreprises</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* CTA */}
                    <section className="mt-12 text-center">
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                Prêt à commencer ?
                            </h3>
                            <p className="text-gray-700 mb-6">
                                Rejoignez les milliers d'entreprises qui font confiance à Netikash
                            </p>
                            <div className="flex flex-wrap gap-4 justify-center">
                                <a
                                    href="https://gateway.netikash.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-600 text-white rounded-lg font-medium hover:from-amber-600 hover:to-yellow-700 transition-all"
                                >
                                    Créer un compte
                                </a>
                                <a
                                    href="/integration"
                                    className="px-6 py-3 bg-white border border-amber-300 text-amber-700 rounded-lg font-medium hover:bg-amber-50 transition-all"
                                >
                                    Guide d'intégration
                                </a>
                                <a
                                    href="/contact"
                                    className="px-6 py-3 bg-white border border-amber-300 text-amber-700 rounded-lg font-medium hover:bg-amber-50 transition-all"
                                >
                                    Nous contacter
                                </a>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </>
    );
};

export default About;